import { useEffect, useState } from "react";
import { useMousePosition } from "../hooks/useMousePosition";

// ============================================================================
// HARDCODED CONFIGURATION - Update all values here
// ============================================================================
const LOCK_SCREEN_CONFIG = {
  // Set to true to unlock the site, false to keep it locked
  SITE_READY: false,

  // System information
  SYSTEM_VERSION: "v0.1.0-beta",
  BUILD_HASH: "945de72",
  SYS_MODE: "LOCKED",
  LAST_UPDATE: "—",
} as const;
// ============================================================================

function ScanningLoader() {
  return (
    <div className="lock-scanning-loader">
      <div className="lock-scanning-line" />
    </div>
  );
}

interface LockScreenProps {
  onUnlock: () => void;
}

export function LockScreen({ onUnlock }: LockScreenProps) {
  const [isDismissing, setIsDismissing] = useState(false);
  const mousePosition = useMousePosition();

  useEffect(() => {
    // Block interaction
    document.body.style.overflow = "hidden";
    document.body.style.pointerEvents = "none";

    // Check if site is ready (hardcoded)
    if (LOCK_SCREEN_CONFIG.SITE_READY) {
      setIsDismissing(true);
      // Wait for fade out animation then unlock
      setTimeout(() => {
        document.body.style.overflow = "";
        document.body.style.pointerEvents = "";
        onUnlock();
      }, 600);
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.pointerEvents = "";
    };
  }, [onUnlock]);

  // Calculate parallax offset for glow (2-3px shift)
  const glowOffsetX = ((mousePosition.x - window.innerWidth / 2) / window.innerWidth) * 3;
  const glowOffsetY = ((mousePosition.y - window.innerHeight / 2) / window.innerHeight) * 3;

  return (
    <div
      id="loading-root"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center h-screen lock-screen-container ${
        isDismissing ? "lock-screen-dismiss" : ""
      }`}
      style={{ pointerEvents: "auto" }}
    >
      {/* Background with enhanced radial vignette */}
      <div className="absolute inset-0 lock-background" />

      {/* Soft spotlight gradient from center */}
      <div className="absolute inset-0 lock-spotlight" />

      {/* Noise overlay with blend mode */}
      <div className="absolute inset-0 lock-noise-overlay" />

      {/* Atmospheric motion blobs */}
      <div className="absolute inset-0 pointer-events-none lock-atmospheric-blobs">
        <div className="lock-blob lock-blob-cyan" />
        <div className="lock-blob lock-blob-blue" />
      </div>

      {/* Scanline - every 12-20 seconds */}
      <div className="absolute inset-0 pointer-events-none lock-scanline" />


      {/* Center glow behind card */}
      <div className="absolute lock-center-glow" />

      {/* Parallax glow behind logo (slightly offset) */}
      <div
        className="absolute lock-logo-glow-orb"
        style={{
          transform: `translate(calc(-50% + ${glowOffsetX}px), calc(-50% + ${glowOffsetY}px))`,
        }}
      />

      {/* System Frame - Hard OS panel */}
      <div className="relative z-10 lock-panel">
        {/* Panel inner noise texture */}
        <div className="absolute inset-0 lock-panel-noise rounded-md pointer-events-none" />

        {/* Logo with backlit glow + chromatic edge */}
        <h1 className="relative z-10 lock-logo font-serif text-[clamp(48px,6vw,80px)] font-bold leading-none text-white/90">
          THEMA
        </h1>

        {/* System lines with hierarchy */}
        <div className="relative z-10 lock-system-lines">
          <p className="lock-system-line-1 lock-os-version">THEMA_OS SYSTEM {LOCK_SCREEN_CONFIG.SYSTEM_VERSION}</p>
          <p className="lock-system-line-2 lock-status-line">STATUS: <span className="text-red-400">OFFLINE</span>  •  <span className="text-yellow-400">ACCESS LOCKED</span></p>
          <p className="lock-system-line-3 lock-module-line">MODULES: INITIALIZING</p>
          <p className="lock-system-line-4 lock-module-line">BOOT SEQUENCE: STANDBY</p>
        </div>

        {/* Scanning loader */}
        <div className="relative z-10">
          <ScanningLoader />
        </div>
      </div>

      {/* Bottom metadata bar with dividers and blinking dot */}
      <div className="absolute bottom-8 left-8 lock-metadata">
        <div className="lock-metadata-line">
          <span className="lock-blink-dot" /> SYS_MODE: {LOCK_SCREEN_CONFIG.SYS_MODE}
        </div>
        <div className="lock-metadata-divider">---</div>
        <div className="mt-1 lock-build-hash">
          BUILD_HASH: {LOCK_SCREEN_CONFIG.BUILD_HASH}
        </div>
        <div className="lock-metadata-divider">---</div>
        <div className="mt-1">LAST_UPDATE: {LOCK_SCREEN_CONFIG.LAST_UPDATE}</div>
      </div>
    </div>
  );
}
